"use client";

import { Check, Clock } from "lucide-react";
import type { BusinessOverview } from "@/modules/catalog/surfaces";
import type { MemberTier } from "@/modules/identity/surfaces";
import { BusinessLogo } from "./BusinessLogo";
import { buttonClass } from "./buttons";
import { DataUnavailable } from "./DataUnavailable";
import { SetupChecklist, type SetupState } from "./SetupChecklist";
import { Card } from "./ui";

/**
 * `/business`, two screens on one route, shared by both frontends (R22 T14):
 *
 * - a member with a business  -> docs/ux/screens/BusinessHomeScreen.md
 * - an unaffiliated member    -> docs/ux/screens/RegistrationStatusScreen.md
 *
 * **Pure render over one payload, and no commands at all.** This screen decides
 * nothing and writes nothing; every card is a link and every count came from
 * the server. So the whole of it moves, with only a `Link` injected, and the
 * two hosts cannot show a clinic a different home.
 *
 * The tier split is H26: a practitioner-tier member gets Bookings and Customers
 * and no business tools, and a manager gets the full set. The **server** decides
 * the tier and every tool re-asks; this only renders which cards exist.
 */

/** The payload the API composes for this route. A union, exactly as it renders. */
export type BusinessHomeView =
  | { kind: "error" }
  | {
      kind: "home";
      businessId: string;
      overview: BusinessOverview;
      tier: MemberTier;
      setup: SetupState;
      /**
       * A registration for an ADDITIONAL clinic, waiting on the Admin (H37).
       * A member with one already has somewhere to work, so the pending state
       * is a panel on their home rather than the whole screen.
       */
      pendingClinic: { businessName: string; submittedAt: string } | null;
    }
  | { kind: "pending"; businessName: string; submittedAt: string }
  | { kind: "none" };

/** How this host renders an in-app link. */
export type HomeLink = (props: {
  href: string;
  className?: string;
  children: React.ReactNode;
}) => React.ReactNode;

/**
 * The whole route, including its three non-home answers: a clinic that could
 * not be read, a registration waiting on the Admin, and a member who has not
 * registered anything yet.
 */
export function BusinessHomeRoute({
  view,
  unread,
  configured = true,
  Link,
}: {
  view: BusinessHomeView;
  /** The inbox count, badged on the way in. */
  unread: number;
  /** False only for a build nobody pointed at a database (README rule 8). */
  configured?: boolean;
  Link: HomeLink;
}) {
  return (
    <>
      <div className="mx-auto mb-4 flex w-full max-w-md items-center justify-end">
        <Link href="/inbox" className="text-sm font-medium text-accent">
          Inbox
          {unread > 0 && (
            <span className="ms-1 rounded-lg bg-accent px-2 py-0.5 text-xs font-bold text-on-accent">
              {unread}
            </span>
          )}
        </Link>
      </div>

      {view.kind === "error" && (
        <Card>
          <DataUnavailable configured={configured} what="your schedule" />
        </Card>
      )}

      {view.kind === "home" && <BusinessHome view={view} Link={Link} />}

      {view.kind === "pending" && (
        <PendingReview
          businessName={view.businessName}
          submittedAt={view.submittedAt}
        />
      )}

      {view.kind === "none" && (
        <Card>
          <h1 className="mb-2 text-xl font-bold text-ink">
            You haven&apos;t registered a business yet.
          </h1>
          <p className="mb-6 text-sm text-body">
            Tell us about your business to get on Bookr.
          </p>
          <Link
            href="/join/business"
            className={buttonClass("primary", "inline-block")}
          >
            Register your business
          </Link>
        </Card>
      )}
    </>
  );
}

/** docs/ux/screens/BusinessHomeScreen.md */
function BusinessHome({
  view,
  Link,
}: {
  view: {
    businessId: string;
    overview: BusinessOverview;
    tier: MemberTier;
    setup: SetupState;
    pendingClinic: { businessName: string; submittedAt: string } | null;
  };
  Link: HomeLink;
}) {
  const { overview, tier, setup, pendingClinic } = view;

  return (
    <div className="mx-auto w-full max-w-md">
      <div className="mb-4 rounded-xl border border-line bg-surface p-6 shadow-sm">
        <div className="flex items-center gap-4">
          <BusinessLogo logoUrl={overview.logoUrl} name={overview.name} size={56} />
          <div>
            <h1 className="text-xl font-bold text-ink">{overview.name}</h1>
          </div>
        </div>
      </div>

      {pendingClinic && <PendingClinicPanel {...pendingClinic} />}

      {tier === "practitioner" ? (
        <PractitionerHome Link={Link} />
      ) : (
        <ManagerHome overview={overview} setup={setup} Link={Link} />
      )}

      {!pendingClinic && (
        <Link
          href="/join/business"
          className="mt-6 block text-center text-sm font-semibold text-accent hover:underline"
        >
          Register another clinic
        </Link>
      )}
    </div>
  );
}

/**
 * The second clinic, waiting on the Admin (H37).
 *
 * Deliberately smaller than the unaffiliated member's
 * [RegistrationStatusScreen](docs/ux/screens/RegistrationStatusScreen.md): that
 * screen is somebody's whole home while they wait, and its three-step list is
 * there because there is nothing else to look at. Here there is a clinic to
 * run, and the wait is a line of news rather than the state of the page.
 */
function PendingClinicPanel({
  businessName,
  submittedAt,
}: {
  businessName: string;
  submittedAt: string;
}) {
  return (
    <div className="mb-4 rounded-xl border border-line bg-surface p-5 shadow-sm">
      <span className="inline-flex items-center gap-2 rounded-lg bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
        <Clock className="size-3.5" aria-hidden />
        Waiting for review
      </span>
      <p className="mt-3 font-semibold text-ink">{businessName}</p>
      <p className="mt-1 text-sm text-muted">
        Submitted {new Date(submittedAt).toLocaleDateString()}. You&apos;ll get a
        notification here the moment it is decided, and it changes nothing about
        the clinic you are working at now.
      </p>
    </div>
  );
}

/**
 * Practitioner-tier home (H26): view-only Bookings + Customers, no business
 * tools.
 */
function PractitionerHome({ Link }: { Link: HomeLink }) {
  return (
    <div className="space-y-3">
      <Link
        href="/business/bookings"
        className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
      >
        <span className="font-semibold text-ink">Bookings</span>
        <span className="ms-2 text-sm text-muted">view the schedule</span>
      </Link>
      <Link
        href="/business/customers"
        className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
      >
        <span className="font-semibold text-ink">Customers</span>
        <span className="ms-2 text-sm text-muted">search by name or phone</span>
      </Link>
    </div>
  );
}

/** Manager-tier home (H26): the full toolset. */
function ManagerHome({
  overview,
  setup,
  Link,
}: {
  overview: BusinessOverview;
  setup: SetupState;
  Link: HomeLink;
}) {
  const gated = overview.serviceCount === 0;

  return (
    <>
      {/* Replaced a single "Set up your services" banner that appeared only at
          zero services and said nothing about the three other things standing
          between a new clinic and its first booking. The gate itself is
          unchanged: the cards below still lock until there is a service. */}
      <SetupChecklist setup={setup} Link={Link} />

      <div className="space-y-3">
        <Link
          href="/business/services"
          className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
        >
          <span className="font-semibold text-ink">Services</span>
          <span className="ms-2 text-sm text-muted">
            {overview.serviceCount} listed
          </span>
        </Link>

        {gated ? (
          <div className="block rounded-xl border border-line bg-surface p-5 opacity-70 shadow-sm">
            <span className="font-semibold text-ink">Practitioners</span>
            <span className="ms-2 rounded-lg bg-line px-2 py-0.5 text-xs font-semibold text-muted">
              Locked
            </span>
            <p className="mt-1 text-sm text-muted">
              Add your first service to unlock practitioner management.
            </p>
          </div>
        ) : (
          <Link
            href="/business/practitioners"
            className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
          >
            <span className="font-semibold text-ink">Practitioners</span>
            <span className="ms-2 text-sm text-muted">
              {overview.activePractitionerCount} active
            </span>
          </Link>
        )}

        {gated ? (
          <div className="block rounded-xl border border-line bg-surface p-5 opacity-70 shadow-sm">
            <span className="font-semibold text-ink">Opening hours</span>
            <span className="ms-2 rounded-lg bg-line px-2 py-0.5 text-xs font-semibold text-muted">
              Locked
            </span>
            <p className="mt-1 text-sm text-muted">
              Add your first service to unlock your opening hours.
            </p>
          </div>
        ) : (
          <Link
            href="/business/hours"
            className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
          >
            <span className="font-semibold text-ink">Opening hours</span>
            <span className="ms-2 text-sm text-muted">
              when the clinic is open
            </span>
          </Link>
        )}

        {gated ? (
          <div className="block rounded-xl border border-line bg-surface p-5 opacity-70 shadow-sm">
            <span className="font-semibold text-ink">Schedule</span>
            <span className="ms-2 rounded-lg bg-line px-2 py-0.5 text-xs font-semibold text-muted">
              Locked
            </span>
            <p className="mt-1 text-sm text-muted">
              Add your first service to unlock your schedule.
            </p>
          </div>
        ) : (
          <Link
            href="/business/schedule"
            className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
          >
            <span className="font-semibold text-ink">Schedule</span>
            <span className="ms-2 text-sm text-muted">
              who works here, and their time off
            </span>
          </Link>
        )}

        {gated ? (
          <div className="block rounded-xl border border-line bg-surface p-5 opacity-70 shadow-sm">
            <span className="font-semibold text-ink">Bookings</span>
            <span className="ms-2 rounded-lg bg-line px-2 py-0.5 text-xs font-semibold text-muted">
              Locked
            </span>
            <p className="mt-1 text-sm text-muted">
              Add your first service to start taking bookings.
            </p>
          </div>
        ) : (
          <Link
            href="/business/bookings"
            className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
          >
            <span className="font-semibold text-ink">Bookings</span>
            <span className="ms-2 text-sm text-muted">
              approve, decline, move, cancel
            </span>
          </Link>
        )}

        <Link
          href="/business/customers"
          className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
        >
          <span className="font-semibold text-ink">Customers</span>
          <span className="ms-2 text-sm text-muted">search by name or phone</span>
        </Link>

        {gated ? (
          <div className="block rounded-xl border border-line bg-surface p-5 opacity-70 shadow-sm">
            <span className="font-semibold text-ink">Dashboard</span>
            <span className="ms-2 rounded-lg bg-line px-2 py-0.5 text-xs font-semibold text-muted">
              Locked
            </span>
            <p className="mt-1 text-sm text-muted">
              Add your first service to see your numbers.
            </p>
          </div>
        ) : (
          <Link
            href="/business/insights"
            className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
          >
            {/* "Dashboard" matches the navigation and the screen itself; the
                card used to say "Insights" and lead to a page titled neither. */}
            <span className="font-semibold text-ink">Dashboard</span>
            <span className="ms-2 text-sm text-muted">
              customers and financials
            </span>
          </Link>
        )}

        <Link
          href="/business/profile"
          className="block rounded-xl border border-line bg-surface p-5 shadow-sm hover:border-accent"
        >
          <span className="font-semibold text-ink">Edit profile</span>
          <span className="ms-2 text-sm text-muted">
            name, categories, location, logo
          </span>
        </Link>
      </div>
    </>
  );
}

/**
 * What a clinic sees between submitting and being approved.
 *
 * It used to be a grey "Pending review" chip and one sentence, which is
 * accurate and reads as a dead end: nothing says how long, what is being
 * checked, or what happens the moment it clears. There is genuinely nothing to
 * configure yet (no Business exists until approval), so the screen cannot offer
 * work. What it can do is set the expectation honestly and show that the thing
 * was received, which is most of what the anxiety is about.
 */
function PendingReview({
  businessName,
  submittedAt,
}: {
  businessName: string;
  submittedAt: string;
}) {
  const steps = [
    { label: "Registration submitted", done: true },
    { label: "We check the details", done: false },
    { label: "Your tools unlock", done: false },
  ];
  return (
    <Card>
      <span className="mb-3 inline-flex items-center gap-2 rounded-lg bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
        <Clock className="size-3.5" aria-hidden />
        Waiting for review
      </span>
      <h1 className="text-xl font-bold text-ink">{businessName}</h1>
      <p className="mt-1 text-sm text-muted">
        Submitted {new Date(submittedAt).toLocaleDateString()}
      </p>

      <ol className="mt-6 space-y-3">
        {steps.map((step, i) => (
          <li key={step.label} className="flex items-center gap-3">
            <span
              className={`flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                step.done
                  ? "bg-primary text-on-primary"
                  : "border border-line bg-surface text-muted"
              }`}
              aria-hidden
            >
              {step.done ? <Check className="size-3.5" /> : i + 1}
            </span>
            <span className={step.done ? "text-sm text-muted" : "text-sm text-ink"}>
              {step.label}
            </span>
          </li>
        ))}
      </ol>

      <p className="mt-6 border-t border-line pt-5 text-sm text-body">
        Nothing else is needed from you. We check that the business is real and
        that the categories fit, then your schedule, services and bookings all
        open at once.
      </p>
      <p className="mt-2 text-sm text-muted">
        You&apos;ll get a notification here the moment it is decided.
      </p>
    </Card>
  );
}
